/**
 * Shared "Cena bez DPH / Cena s DPH / DPH" trio for výbava (equipment) rows.
 *
 * Dual-VAT entry: the user may type either the price excl. or incl. VAT and the counterpart is
 * recomputed from the row's DPH rate. Only `cena` (bez DPH) is persisted — the
 * s-DPH value is a UI mirror, so consumers strip it before saving.
 *
 * Used by the offer wizard, the contract equipment modal, and the vehicle form.
 */
import { Field } from '@/components/Field';
import { SelectInput } from '@/components/SelectInput';
import { TextInput } from '@/components/TextInput';

interface VatRateLike {
  id_dph: number;
  hodnota: number;
  nazev: string;
}

/** VAT multiplier for a rate id (1.21, 1.15, …); defaults to 1.21 like legacy. */
export function vatMult(rates: { id_dph: number; hodnota: number }[], idDph: number): number {
  const found = rates.find((r) => r.id_dph === idDph);
  if (!found) return 1.21;
  return Number(found.hodnota) || 1.21;
}

/** Derive the with-VAT price from a bez-DPH price. Blank/invalid in → blank out. */
export function sDphFromBez(
  cena: string,
  idDph: number,
  rates: { id_dph: number; hodnota: number }[],
): string {
  if (!cena.trim()) return '';
  const n = parseFloat(cena);
  if (!Number.isFinite(n)) return '';
  return (n * vatMult(rates, idDph)).toFixed(2);
}

/** Derive the bez-DPH price from a with-VAT price. Blank/invalid in → blank out. */
export function bezFromSDph(
  cenaSDph: string,
  idDph: number,
  rates: { id_dph: number; hodnota: number }[],
): string {
  if (!cenaSDph.trim()) return '';
  const n = parseFloat(cenaSDph);
  if (!Number.isFinite(n)) return '';
  return (n / vatMult(rates, idDph)).toFixed(2);
}

export interface EquipmentPriceValue {
  /** Persisted price, excl. VAT. */
  cena: string;
  /** UI-only mirror, incl. VAT. Strip before saving. */
  cenaSDph: string;
  id_dph: number;
}

/**
 * Renders three `<Field>`s (bez DPH · s DPH · DPH). Editing either price field
 * recomputes the other; changing the rate recomputes the s-DPH mirror from the
 * bez-DPH source of truth. Place inside a grid whose template reserves columns
 * for all three.
 */
export function EquipmentPriceFields({
  value,
  vatRates,
  onChange,
}: {
  value: EquipmentPriceValue;
  vatRates: VatRateLike[];
  onChange: (next: EquipmentPriceValue) => void;
}) {
  const onBez = (cena: string) =>
    onChange({ ...value, cena, cenaSDph: sDphFromBez(cena, value.id_dph, vatRates) });
  const onSDph = (cenaSDph: string) =>
    onChange({ ...value, cenaSDph, cena: bezFromSDph(cenaSDph, value.id_dph, vatRates) });
  const onDph = (id_dph: number) =>
    onChange({ ...value, id_dph, cenaSDph: sDphFromBez(value.cena, id_dph, vatRates) });

  return (
    <>
      <Field label="Cena (bez DPH)">
        <TextInput
          type="number"
          value={value.cena}
          onChange={(e) => onBez(e.currentTarget.value)}
          suffix="Kč"
        />
      </Field>
      <Field label="Cena (s DPH)">
        <TextInput
          type="number"
          value={value.cenaSDph}
          onChange={(e) => onSDph(e.currentTarget.value)}
          suffix="Kč"
        />
      </Field>
      <Field label="DPH">
        <SelectInput value={String(value.id_dph)} onChange={(e) => onDph(Number(e.currentTarget.value) || 0)}>
          <option value="0">—</option>
          {vatRates.map((v) => (
            <option key={v.id_dph} value={v.id_dph}>
              {v.nazev}
            </option>
          ))}
        </SelectInput>
      </Field>
    </>
  );
}
